import { computed, Injectable, signal } from '@angular/core';
import { Params } from '@angular/router';

import {
    LiquidViewerIndex,
    LiquidViewerIndexItem,
    LiquidViewerIndexParagraph,
} from '../liquid-viewer.models';
import {
    environment
} from 'src/index';

@Injectable({
    providedIn: 'root',
})
export class LiquidViewerIndexService {
    $currentIndexItemId = signal<string | undefined>(undefined);
    $pages = signal<{ [key: string]: LiquidViewerIndexItem }>({});
    $pagesCounter = signal<number>(0);

    $currentIndexItem = computed(() => {
        const id = this.$currentIndexItemId();
        return id ? this.$pages()[id] : undefined;
    });
    $next = computed(() => this.$currentIndexItem()?.nextPage);
    $previous = computed(() => this.$currentIndexItem()?.previousPage);

    reset() {
        this.$currentIndexItemId.set(undefined);
    }

    /* 
    2 base cases
    - type = 'page'
        - get previous page and save in previousPageId
        - get next page id and save in nextPageId
        - if paragraphs, foreach paragraphs add pageId
    - type = 'chapter'
        - if children, foreach children add chapterId and if children type = 'page' same of type 'page' case
    */

    prepareIndex(index: LiquidViewerIndex) {
        let lastPage: LiquidViewerIndexItem | undefined = undefined;

        index.items.forEach((item, itemPosition) => {

            item.a11yRoute = this.getA11yRoute(item);

            if (item.type === 'chapter') {
                let result = this.prepareChapterIndexItem(item);
                index.items[itemPosition] = result.item;
                if (result.lastPage) {
                    lastPage = result.lastPage;
                }
            }

            if (item.type === 'page') {
                this.$pagesCounter.update(cv => cv += 1);
                item.pageNumber = this.$pagesCounter();
                this.$pages.update(cv => ({
                    ...cv,
                    [item.id]: item,
                }));

                index.items[itemPosition] = this.preparePageIndexItem(item);
                if (lastPage) {
                    index.items[itemPosition].previousPage = lastPage;
                }

                let nextPage = this.findNextPageInIndexItemArray(index.items, itemPosition);

                // In presenza di children
                if (index.items[itemPosition].children && index.items[itemPosition].children!.length > 0) {

                    index.items[itemPosition].nextPage = index.items[itemPosition].children![0];

                    index.items[itemPosition].children!.forEach((childrenItem, childrenItemIndex) => {
                        index.items[itemPosition].children![childrenItemIndex] = this.preparePageIndexItem(childrenItem);

                        // La prima sottopagina ha come precedente la pagina, le restanti la precedente sottopagina
                        if (childrenItemIndex === 0) {
                            index.items[itemPosition].children![childrenItemIndex].previousPage = item;
                        } else {
                            index.items[itemPosition].children![childrenItemIndex].previousPage = lastPage;
                        }
                        lastPage = childrenItem;

                        // L'ultima sottopagina ha come successiva la prima pagina successiva, le restanti la successiva sottopagina
                        if (childrenItemIndex === index.items[itemPosition].children!.length - 1) {
                            index.items[itemPosition].children![childrenItemIndex].nextPage = nextPage;
                        } else {
                            index.items[itemPosition].children![childrenItemIndex].nextPage =
                                index.items[itemPosition].children![childrenItemIndex + 1];
                        }

                        this.$pagesCounter.update(cv => cv += 1);
                        childrenItem.pageNumber = this.$pagesCounter();
                        this.$pages.update(cv => ({
                            ...cv,
                            [childrenItem.id]: childrenItem,
                        }));
                    })

                } else {
                    lastPage = item;
                    if (nextPage) { // Se non mi torna nulla, significa che non vi sono altre pagine nelle successive posizioni
                        index.items[itemPosition].nextPage = nextPage;
                    }
                }
            }
        });

        if (!environment.viewerModule?.disableModuleNavigation) {
            index.items.forEach((item, itemPos) => {
                // Handle next page
                if (item.type === 'chapter') {
                    const lastPage = this.findLastPageInIndexItemArray(item.children ?? []);
                    if (lastPage && !lastPage.nextPage) {
                        if (itemPos < index.items.length) {
                            const nextPage = this.findNextPageInIndexItemArray(index.items, itemPos);
                            lastPage.nextPage = nextPage;
                        }
                    }
                } else {
                    if (!item.nextPage) {
                        const nextPage = this.findNextPageInIndexItemArray(index.items, itemPos);
                        item.nextPage = nextPage;
                    }
                }

                // Handle previous page 
                if (itemPos > 0) {
                    const previousItem = index.items[itemPos - 1];
                    const previousPage = previousItem.type === 'page' ? previousItem : this.findLastPageInIndexItemArray(previousItem.children ?? []);
                    if (item.type === 'chapter') {
                        const firstPage = this.findNextPageInIndexItemArray(item.children ?? []);
                        if (firstPage && !firstPage.previousPage) {
                            firstPage.previousPage = previousPage;
                        }
                    } else {
                        if (!item.previousPage) {
                            item.previousPage = previousPage;
                        }
                    }
                }
            });
        }
        return index;
    }

    prepareChapterIndexItem(item: LiquidViewerIndexItem, parentChapter?: LiquidViewerIndexItem, positionInParentChapter?: number) {
        let lastPage: LiquidViewerIndexItem | undefined = undefined;
        item.a11yRoute = this.getA11yRoute(item);

        if (item.children && item.children.length > 0) {
            item.children.forEach((child, idx) => {
                if (item.children) {
                    item.children[idx].parentChapter = item;

                    if (child.type === 'chapter') {
                        let result = this.prepareChapterIndexItem(child, item, idx);
                        item.children[idx] = result.item;
                        if (result.lastPage) {
                            lastPage = result.lastPage;
                        }
                    }

                    if (child.type === 'page') {
                        this.$pagesCounter.update(cv => cv += 1);
                        child.pageNumber = this.$pagesCounter();
                        this.$pages.update(cv => ({
                            ...cv,
                            [child.id]: child,
                        }));

                        item.children[idx] = this.preparePageIndexItem(child);
                        if (lastPage) {
                            item.children[idx].previousPage = lastPage;
                        } else if (parentChapter && parentChapter.children && positionInParentChapter !== undefined) {
                            const previousPage = this.findLastPageInIndexItemArray(parentChapter.children, positionInParentChapter);
                            if (previousPage) {
                                item.children[idx].previousPage = previousPage;
                            }
                            // Mi fermo a questo punto perchè teoricamente non può esistere un capitolo senza pagina
                            // Per cui, se la funzione mi restituisce null, significa che mi trovo all'ultima pagina di un sottocapitolo e non ci sono altri capitoli o pagine dopo
                        }

                        let nextPage = this.findNextPageInIndexItemArray(item.children, idx);

                        // In presenza di sottopagine
                        if (child.children && child.children.length > 0) {
                            item.children[idx].nextPage = child.children![0];

                            child.children.forEach((childrenItem, childrenItemIndex) => {
                                child.children![childrenItemIndex] = this.preparePageIndexItem(childrenItem);

                                // La prima sottopagina ha come precedente la pagina, le restanti la precedente sottopagina
                                if (childrenItemIndex === 0) {
                                    child.children![childrenItemIndex].previousPage = child;
                                } else {
                                    child.children![childrenItemIndex].previousPage = lastPage;
                                }
                                lastPage = childrenItem;

                                // L'ultima sottopagina ha come successiva la prima pagina successiva, le restanti la successiva sottopagina
                                if (childrenItemIndex === child.children!.length - 1) {
                                    if (nextPage) {
                                        child.children![childrenItemIndex].nextPage = nextPage;
                                    } else if (parentChapter && parentChapter.children) {
                                        child.children![childrenItemIndex].nextPage = this.findNextPageInIndexItemArray(parentChapter.children, positionInParentChapter)
                                    }
                                } else {
                                    child.children![childrenItemIndex].nextPage = child.children![childrenItemIndex + 1];
                                }

                                this.$pagesCounter.update(cv => cv += 1);
                                childrenItem.pageNumber = this.$pagesCounter();
                                this.$pages.update(cv => ({
                                    ...cv,
                                    [childrenItem.id]: childrenItem,
                                }));
                            })

                        } else {
                            lastPage = child;
                            if (nextPage) {
                                item.children[idx].nextPage = nextPage;
                            } else {
                                // Se non mi torna nulla, significa che non vi sono altre pagine nelle successive posizioni
                                // In questo caso, trovandomi in un capitolo, se il capitolo si trova al livello 0
                                // allora devo verificare se ci sono altre pagine nelle successive posizioni di "index.items"
                                // Se, invece, il livello di annidamento è superiore, allora devo risalire al livello precedente e verificare la stessa cosa
                                if (parentChapter && parentChapter.children && positionInParentChapter !== undefined) {
                                    nextPage = this.findNextPageInIndexItemArray(parentChapter.children, positionInParentChapter);
                                    if (nextPage) {
                                        item.children[idx].nextPage = nextPage;
                                    }
                                    // Mi fermo a questo punto perchè teoricamente non può esistere un capitolo senza pagina
                                    // Per cui, se la funzione mi restituisce null, significa che mi trovo all'ultima pagina di un sottocapitolo e non ci sono altri capitoli o pagine dopo
                                }
                            }
                        }


                    }
                }
            })
        }

        return { item, lastPage };
    }

    preparePageIndexItem(item: LiquidViewerIndexItem) {
        item.a11yRoute = this.getA11yRoute(item);
        if (item.paragraphs && item.paragraphs.length > 0) {
            item.paragraphs.forEach((_, idx) => {
                if (item.paragraphs) {
                    item.paragraphs[idx].page = item;
                }
            })
        }

        return item;
    }

    // ------- Search functions -------------
    findNextPageInIndexItemArray(inputArray: LiquidViewerIndexItem[], startPosition: number = -1): LiquidViewerIndexItem | undefined {
        let nextItem = inputArray[startPosition + 1];
        if (nextItem && nextItem.type === 'page') {
            return nextItem;
        }

        if (nextItem && nextItem.type === 'chapter' && nextItem.children && nextItem.children.length > 0) {
            return this.findNextPageInIndexItemArray(nextItem.children);
        }

        return;
    }

    findLastPageInIndexItemArray(inputArray: LiquidViewerIndexItem[], startPosition?: number): LiquidViewerIndexItem | undefined {
        startPosition = startPosition !== undefined ? startPosition : inputArray.length;
        let previousItem = inputArray[startPosition - 1];
        if (previousItem && previousItem.type === 'page') {
            return previousItem;
        }

        if (previousItem && previousItem.type === 'chapter' && previousItem.children && previousItem.children.length > 0) {
            return this.findLastPageInIndexItemArray(previousItem.children);
        }

        return;
    }

    // ------- END Search functions -------------

    // ------- Accessibility functions -------------
    /**
     * Management of links with active href, to be used as link accessibility information, not to be used as navigation.
     * @param indexItem 
     * @returns 
     */
    getA11yRoute(indexItem: LiquidViewerIndexItem | LiquidViewerIndexParagraph | undefined): { route: string, queryParams: Params | null } {
        let route: string = location.pathname;
        let params: Params | null = null;
        if (indexItem) {
            switch (indexItem.type) {
                case 'paragraph':
                    if (indexItem.page && indexItem.page.parentChapter) {
                        const locationArray = window.location.pathname.split('/')
                        locationArray.pop();
                        route = locationArray.join('/') + '/' + indexItem.page.id;
                        params = { paragraph: indexItem.id };
                    }
                    return { route: route, queryParams: params };
                case 'page':
                    if (indexItem.parentChapter) {
                        const locationArray = window.location.pathname.split('/')
                        locationArray.pop();
                        route = locationArray.join('/') + '/' + indexItem.id;
                    }
                    return { route: route, queryParams: params };
                case 'chapter':
                    if (indexItem.children && indexItem.children[0]) {
                        const locationArray = window.location.pathname.split('/')
                        locationArray.pop();
                        route = locationArray.join('/') + '/' + indexItem.children[0].id;
                    }
                    return { route: route, queryParams: params };
                default:
                    return { route: route, queryParams: params };
            }
        }
        else {
            return { route: route, queryParams: params };
        }
    }
    // ------- END Accessibility functions -------------
}